import React, { useState, useCallback, useRef } from 'react';
import BottomNav from '../../components/BottomNav';
import { BINGO_CHALLENGES } from '../../data/gamesData';
import ScoreCard from '../../components/ScoreCard';
import { useGame, HINT_COSTS } from '../../context/GameContext';
import { shuffle, HintBubble, PauseOverlay, ShareBtn, ScoreImageBtn, BibleRef, RulesBtn , xpShareLine, xpForCard , isAnswerCorrect } from './shared';
import { fetchQuestions } from '../../utils/questionsAPI';
import LoadingScreen from '../../components/LoadingScreen';

const CELL_ICONS = { text: '✍️', mc: '📋', tf: '⚖️' };
const apiToCell = (q) => ({
  ...q,
  icon:  CELL_ICONS[q.type] || '📖',
  label: q.q.length > 20 ? q.q.slice(0, 20) + '…' : q.q,
});

// ── makeGrid : 24 défis + case libre, plus une réserve pour rotation ─────────
// Accepte des cellules API supplémentaires mélangées avec le pool local.
const makeGrid = (extra = []) => {
  const localPool = shuffle(BINGO_CHALLENGES.filter(c => c.type !== 'free'));
  const combined  = shuffle([...extra, ...localPool]);
  const playing   = combined.slice(0, 24);
  const reserve   = combined.slice(24);
  const free      = BINGO_CHALLENGES.find(c => c.type === 'free');
  const cells     = [...playing.slice(0, 12), free, ...playing.slice(12, 24)];
  return { cells, reserve };
};

const checkBingo = (completed) => {
  const lines = [];
  for (let r = 0; r < 5; r++) if ([0,1,2,3,4].map(c => r*5+c).every(i => completed.has(i))) lines.push(`r${r}`);
  for (let c = 0; c < 5; c++) if ([0,1,2,3,4].map(r => r*5+c).every(i => completed.has(i))) lines.push(`c${c}`);
  if ([0,6,12,18,24].every(i => completed.has(i))) lines.push('d1');
  if ([4,8,12,16,20].every(i => completed.has(i))) lines.push('d2');
  return lines;
};

// Composant interne réinitialisé via key à chaque "Rejouer"
const BingoBoard = ({ onBack, onEnd, onReplay, correctionMode, extraCells = [] }) => {
  const { profile } = useGame();
  const [grid, setGrid]             = useState(() => makeGrid(extraCells));   // { cells, reserve }
  const cells = grid.cells;
  const [completed, setCompleted]   = useState(new Set([12]));
  const [activeIdx, setActiveIdx]   = useState(null);
  const [input, setInput]           = useState('');
  const [bingoLines, setBingoLines] = useState([]);
  // fix V1 : indices cumulés sur toute la grille, envoyés en une seule session.
  const hintsUsedRef                = useRef(0);
  const sessionFiredRef             = useRef(false);
  // XP confirmée par le serveur — undefined = en attente, null = échec.
  const [serverXp, setServerXp]     = useState(undefined);
  const [paused, setPaused]         = useState(false);
  const [showHint, setShowHint]     = useState(false);
  const [copied, setCopied]         = useState(false);
  const [showScoreCard, setShowScoreCard] = useState(false);
  // Correction immédiate : feedback visuel sur la case
  const [wrongFlash, setWrongFlash] = useState(null);

  // fix V1 : une grille = une session. La cellule ne déclenche plus de
  // reportSession ; l'unique session part quand les 24 cases répondables sont
  // validées. La case libre (index 12) est pré-validée et ne compte pas.
  const completeCell = useCallback((idx, hintUsed = false) => {
    const nc = new Set([...completed, idx]);
    setCompleted(nc);
    if (hintUsed) hintsUsedRef.current += 1;
    setBingoLines(checkBingo(nc));
    setActiveIdx(null);
    setInput('');
    setShowHint(false);

    // 24 cases répondables + la case libre = 25 entrées dans `completed`
    if (nc.size === cells.length && !sessionFiredRef.current) {
      sessionFiredRef.current = true;
      Promise.resolve(onEnd?.({
        correct:   cells.length - 1,
        total:     cells.length - 1,
        hintsUsed: hintsUsedRef.current,
      }))
        .then(r => setServerXp(r && typeof r.xpGained === 'number' ? r.xpGained : null))
        .catch(() => setServerXp(null));
    }
  }, [completed, cells.length, onEnd]);

  const handleClick = (idx) => {
    if (completed.has(idx)) return;   // inclut la case libre, pré-validée
    if (activeIdx === idx) { setActiveIdx(null); setShowHint(false); return; }
    setActiveIdx(idx);
    setInput('');
    setShowHint(false);
  };

  const rotateCell = (idx) => {
    // Remplace la question de la case par la prochaine de la réserve
    setGrid(g => {
      if (g.reserve.length === 0) return g;
      const [alt, ...rest] = g.reserve;
      const newCells = g.cells.map((c, i) => i === idx ? alt : c);
      return { cells: newCells, reserve: rest };
    });
  };

  const validateAnswer = (ans) => {
    if (activeIdx === null) return;
    if (isAnswerCorrect(ans, cells[activeIdx].a, cells[activeIdx].accept)) {
      completeCell(activeIdx, showHint);
    } else {
      // Mauvaise réponse → rotation vers une nouvelle question + feedback
      const savedIdx = activeIdx;
      if (correctionMode === 'immediate') {
        setWrongFlash({ idx: savedIdx, correct: cells[savedIdx].a, given: ans });
        setTimeout(() => {
          setWrongFlash(null);
          setActiveIdx(null);
          setInput('');
          setShowHint(false);
          rotateCell(savedIdx); // Nouvelle question pour cette case
        }, 2000);
      } else {
        setActiveIdx(null);
        setInput('');
        setShowHint(false);
        rotateCell(savedIdx); // Silencieuse en mode résumé
      }
    }
  };

  const handleShare = () => {
    const text = `🎯 Bible Bingo — ${profile.name || 'Joueur'}\n✅ ${completed.size - 1}/24 cases\n🎉 ${bingoLines.length} BINGO${bingoLines.length > 1 ? 's' : ''}${xpShareLine(serverXp)}\n🎮 Manna`;
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const active = activeIdx !== null ? cells[activeIdx] : null;

  return (
    <div className="page-content">
      {paused && <PauseOverlay onResume={() => setPaused(false)} onQuit={onBack} />}
      {showScoreCard && (
        <ScoreCard
          gameName="Bible Bingo" playerName={profile.name || 'Joueur'} playerAvatar={profile.avatar}
          score={`${completed.size - 1}/24`} xp={xpForCard(serverXp)}
          details={[{ label: 'Bingos', value: bingoLines.length }, { label: 'Cases validées', value: completed.size - 1 }]}
          onClose={() => setShowScoreCard(false)}
        />
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.75rem' }}>
        <button className="btn btn-ghost" onClick={onBack}>← Retour</button>
        <span style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', fontSize:'.9rem' }}>BIBLE BINGO</span>
        <div style={{ display:'flex', gap:'.4rem', alignItems:'center' }}>
          <span style={{ color:'var(--sage-light)', fontFamily:'var(--font-display)', fontWeight:700, fontSize:'.9rem' }}>{completed.size - 1}/{cells.length - 1}</span>
          <RulesBtn gameId="bingo" />
          <button onClick={() => setPaused(true)} style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.1)', borderRadius:6, color:'var(--parch)', cursor:'pointer', padding:'.25rem .45rem', fontSize:'.8rem' }}>⏸</button>
        </div>
      </div>

      {bingoLines.length > 0 && (
        <div className="card" style={{ background:'rgba(201,168,76,.12)', borderColor:'rgba(201,168,76,.4)', textAlign:'center', marginBottom:'.75rem' }}>
          <span style={{ color:'var(--gold-light)', fontWeight:700 }}>🎉 BINGO ×{bingoLines.length} !</span>
        </div>
      )}

      {/* Correction immédiate overlay */}
      {wrongFlash && (
        <div style={{ background:'rgba(230,57,70,.15)', border:'1.5px solid var(--crimson)', borderRadius:10, padding:'.75rem 1rem', marginBottom:'.75rem', animation:'fadeUp .2s ease both' }}>
          <div style={{ fontSize:'.85rem', color:'var(--crimson)', fontWeight:700, marginBottom:'.2rem' }}>❌ Mauvaise réponse</div>
          <div style={{ fontSize:'.8rem', color:'var(--gray-400)' }}>Ta réponse : <b>{wrongFlash.given || '(vide)'}</b></div>
          <div style={{ fontSize:'.85rem', color:'var(--sage-light)', marginTop:'.2rem' }}>✓ Bonne réponse : <b>{wrongFlash.correct}</b></div>
          {cells[wrongFlash.idx]?.ref && <BibleRef verse={cells[wrongFlash.idx].ref} />}
        </div>
      )}

      {/* Grille 5×5 */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'.22rem', marginBottom:'1rem' }}>
        {cells.map((cell, i) => (
          <div key={i} onClick={() => handleClick(i)}
            className={`bingo-cell ${completed.has(i) ? 'completed' : activeIdx === i ? 'active' : ''}`}
            style={{ height:56 }}>
            <span style={{ fontSize:'.85rem' }}>{completed.has(i) ? '✅' : cell.icon}</span>
            <span style={{ fontSize:'.45rem', color:completed.has(i) ? 'var(--sage-light)' : 'var(--gray-400)', marginTop:'.08rem', lineHeight:1.2 }}>
              {cell.label}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.5rem' }}>
        <button className="btn btn-secondary" style={{ fontSize:'.8rem', padding:'.35rem .75rem' }} onClick={onReplay}>
          🔄 Nouvelle grille
        </button>
        <div style={{ display:'flex', gap:'.4rem' }}>
          <ShareBtn onClick={handleShare} copied={copied} />
          <ScoreImageBtn onClick={() => setShowScoreCard(true)} />
        </div>
      </div>

      {active && !wrongFlash && (
        <div className="card" style={{ border:'1.5px solid rgba(201,168,76,.4)', animation:'fadeUp .2s ease both' }}>
          <p style={{ fontWeight:700, color:'var(--gold-light)', marginBottom:'.75rem', fontSize:'.9rem' }}>
            {active.icon} {active.q}
          </p>

          {active.type === 'tf' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem' }}>
              {['Vrai','Faux'].map(opt => (
                <button key={opt} onClick={() => validateAnswer(opt)} style={{ padding:'.7rem', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'var(--font-display)', fontWeight:700, background:opt==='Vrai'?'var(--sage)':'var(--crimson)', color:'white' }}>
                  {opt === 'Vrai' ? '✓ Vrai' : '✗ Faux'}
                </button>
              ))}
            </div>
          )}
          {active.type === 'mc' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'.4rem' }}>
              {active.opts.map(opt => <button key={opt} className="answer-btn" onClick={() => validateAnswer(opt)}>{opt}</button>)}
            </div>
          )}
          {active.type === 'text' && (
            <div style={{ display:'flex', gap:'.5rem' }}>
              <input className="game-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key==='Enter' && validateAnswer(input)} autoFocus placeholder="Votre réponse..." />
              <button className="btn btn-primary" onClick={() => validateAnswer(input)}>✓</button>
            </div>
          )}
          {active.hint && (
            showHint
              ? <HintBubble hint={active.hint} />
              : <button className="btn btn-ghost" onClick={() => setShowHint(true)} style={{ marginTop:'.5rem', fontSize:'.8rem' }}>💡 Indice <span style={{ color:'var(--crimson)', fontSize:'.7rem' }}>−{HINT_COSTS.bingo} XP sur le gain</span></button>
          )}
          {active.ref && <BibleRef verse={active.ref} />}
          <button className="btn btn-ghost" onClick={() => { setActiveIdx(null); setShowHint(false); }} style={{ marginTop:'.5rem', fontSize:'.8rem' }}>
            Annuler
          </button>
        </div>
      )}
      <BottomNav />
    </div>
  );
};

// Wrapper avec écran de choix mode correction + key pour reset total
const BingoGame = ({ onBack, onEnd }) => {
  const [phase, setPhase]                   = useState('choose');
  const [correctionMode, setCorrectionMode] = useState('immediate');
  const [gameKey, setGameKey]               = useState(0);
  const [extraCells, setExtraCells]         = useState([]);

  const launch = async () => {
    setPhase('loading');
    const { questions: qs } = await fetchQuestions({ n: 30 });
    if (qs.length > 0) setExtraCells(qs.map(apiToCell));
    setPhase('playing');
  };

  if (phase === 'loading') return <LoadingScreen />;

  if (phase === 'choose') return (
    <div className="page-content">
      <button className="btn btn-ghost" onClick={onBack}>← Retour</button>
      <div style={{ textAlign:'center', margin:'1.5rem 0' }}>
        <div style={{ fontSize:'4rem' }}>🎯</div>
        <h2 style={{ margin:'.5rem 0 .3rem' }}>Bible Bingo</h2>
        <p className="text-small">Grille 5×5 · Lignes = bonus XP · Questions variées</p>
      </div>
      <div className="card" style={{ marginBottom:'1.25rem' }}>
        <p style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', fontSize:'.9rem', marginBottom:'.75rem' }}>📋 Mode de correction</p>
        {[
          { key:'immediate', icon:'⚡', title:'Correction immédiate', desc:'La bonne réponse apparaît pendant 2s après chaque erreur' },
          { key:'recap',     icon:'📝', title:'Résumé en fin',        desc:'Les erreurs sont visibles uniquement quand tu fermes une case' },
        ].map(({ key, icon, title, desc }) => (
          <div key={key} onClick={() => setCorrectionMode(key)} style={{ display:'flex', alignItems:'flex-start', gap:'.75rem', padding:'.75rem', borderRadius:10, marginBottom:'.5rem', cursor:'pointer', background:correctionMode===key?'rgba(201,168,76,.15)':'rgba(255,255,255,.03)', border:`1.5px solid ${correctionMode===key?'rgba(201,168,76,.5)':'rgba(255,255,255,.06)'}`, transition:'all .2s' }}>
            <div style={{ fontSize:'1.4rem', flexShrink:0, marginTop:'.05rem' }}>{icon}</div>
            <div>
              <div style={{ fontFamily:'var(--font-display)', color:'var(--parch)', fontSize:'.9rem', fontWeight:700 }}>{title}</div>
              <div className="text-tiny" style={{ marginTop:'.2rem', lineHeight:1.4 }}>{desc}</div>
            </div>
            {correctionMode===key && <div style={{ marginLeft:'auto', color:'var(--gold-light)', fontSize:'1.1rem' }}>✓</div>}
          </div>
        ))}
      </div>
      <button className="btn btn-primary w-full" style={{ padding:'1rem', fontSize:'1.1rem' }} onClick={launch}>
        Lancer le Bingo →
      </button>
      <BottomNav />
    </div>
  );

  return (
    <BingoBoard
      key={gameKey}
      onBack={onBack}
      onEnd={onEnd}
      correctionMode={correctionMode}
      extraCells={extraCells}
      onReplay={() => setGameKey(k => k + 1)}
    />
  );
};

export default BingoGame;
